/**
 * moneroBalanceCache — tiny IN-MEMORY wrapper for last-known balance
 * / scan-progress per address.
 *
 * Before the May 2026 privacy audit this module persisted the cache
 * to `localStorage` keyed by `xmr:bal:v1:<network>:<address>`. That
 * meant a Monero wallet's address, balance, transaction list and
 * scan history all sat on disk in plain text — a clear privacy leak
 * because the address is wallet-identifying and the balance / tx
 * list is private financial information that the user never opted
 * into saving. The cache is now session-scoped (one `Map` keyed by
 * `network:address`); reopening the tab forces a fresh LWS poll.
 *
 * The motivation is purely UX: reopening a Monero wizard otherwise
 * shows "0 XMR / 0% / Updated never" for ~5–15 s while the polling
 * tick fetches a fresh snapshot from the LWS. By priming state with
 * the last cached snapshot the panel comes up populated, then the
 * normal poll loop overwrites the cache with a fresh response.
 *
 * Scope:
 *  - One entry per (network, address) inside a single `Map`.
 *  - In-memory only. Nothing reaches disk.
 *  - 24-hour TTL — caches older than that are treated as missing so a
 *    long-running tab doesn't surface misleading numbers if a Monero
 *    sync hasn't run in a day.
 */

import type { MoneroBalanceSnapshot } from './moneroAccount';
import type { LwsAddressTxs } from './moneroLwsClient';

const LEGACY_STORAGE_KEY_PREFIX = 'xmr:bal:v1:';
const DEFAULT_TTL_MS = 24 * 60 * 60 * 1000; // 24 hours

/** Single in-memory snapshot store, keyed by `<network>:<address>`. */
const _balanceCache = new Map<string, CachedMoneroState>();

/** Forward declaration so the `Map` typing above resolves. The
 *  interface body is the existing one below. */
export interface CachedMoneroState {
	address: string;
	network: string;
	balance: MoneroBalanceSnapshot;
	txs?: LwsAddressTxs;
	isScanPaused: boolean;
	cachedAt: number;
}

/**
 * Sweep any legacy `xmr:bal:v1:*` entries an older build left in
 * `localStorage`. Idempotent and safe to call from boot.
 */
let _legacySweepDone = false;
export function sweepLegacyMoneroBalanceCache(): void {
	if (_legacySweepDone) return;
	_legacySweepDone = true;
	if (typeof localStorage === 'undefined') return;
	try {
		const doomed: string[] = [];
		for (let i = 0; i < localStorage.length; i += 1) {
			const key = localStorage.key(i);
			if (key && key.startsWith(LEGACY_STORAGE_KEY_PREFIX)) {
				doomed.push(key);
			}
		}
		for (const key of doomed) {
			try { localStorage.removeItem(key); } catch { /* */ }
		}
		if (doomed.length > 0) {
			console.info('[xmr-balance-cache] swept %d legacy entries',
				doomed.length);
		}
	} catch (err) {
		console.warn('[xmr-balance-cache] legacy sweep failed:',
			(err as Error).message);
	}
}

/** Test-only: wipe the in-memory cache between specs. */
export function __resetMoneroBalanceCacheForTests(): void {
	_balanceCache.clear();
	_legacySweepDone = false;
}

/**
 * Read the last cached snapshot for the given address. Returns `null`
 * if no entry exists or the entry is older than the supplied TTL
 * (defaults to {@link DEFAULT_TTL_MS}).
 */
export function readMoneroBalanceCache(
	address: string | null | undefined,
	network: string,
	options: { now?: () => number; ttlMs?: number } = {},
): CachedMoneroState | null {
	if (!address) return null;
	const key = makeKey(network, address);
	const entry = _balanceCache.get(key);
	if (!entry) return null;
	const ttl = options.ttlMs ?? DEFAULT_TTL_MS;
	const now = (options.now ?? Date.now)();
	if (!entry.cachedAt || now - entry.cachedAt > ttl) {
		_balanceCache.delete(key);
		return null;
	}
	// Defensive shallow copy so callers can't mutate the live entry.
	return {
		address: entry.address,
		network: entry.network,
		balance: { ...entry.balance },
		txs: entry.txs,
		isScanPaused: entry.isScanPaused,
		cachedAt: entry.cachedAt,
	};
}

/**
 * Cache the latest snapshot for the given address (in-memory).
 */
export function writeMoneroBalanceCache(entry: CachedMoneroState): void {
	if (!entry?.address) return;
	const key = makeKey(entry.network, entry.address);
	_balanceCache.set(key, {
		address: entry.address,
		network: entry.network,
		balance: { ...entry.balance },
		txs: entry.txs,
		isScanPaused: entry.isScanPaused,
		cachedAt: entry.cachedAt,
	});
}

/**
 * Remove the cache entry for the given address. Exposed so the
 * receive-panel can wipe stale state after a rescan that throws off
 * the cached progress (e.g. user rolled the scan height back).
 */
export function clearMoneroBalanceCache(
	address: string | null | undefined,
	network: string,
): void {
	if (!address) return;
	_balanceCache.delete(makeKey(network, address));
}

function makeKey(network: string, address: string): string {
	return `${network}:${address}`;
}
